import { useEffect, useMemo, useState } from 'react';
import { Bell, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Switch } from '@/shared/ui/switch';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/domains/accounts/services/notification-preferences';
import {
  groupPreferencesByCategory,
  preferencesToMap,
} from '@/shared/types/notificationTypes';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import EmptyView from '@/shared/components/EmptyView';

type PreferenceMap = Record<string, boolean>;

function mapsEqual(a: PreferenceMap, b: PreferenceMap): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (Boolean(a[key]) !== Boolean(b[key])) return false;
  }
  return true;
}

export const NotificationsSection = () => {
  const { data: preferences, isLoading, error, refetch } = useNotificationPreferences(true);
  const updatePreferences = useUpdateNotificationPreferences();

  const serverPreferences = useMemo(() => preferencesToMap(preferences), [preferences]);
  const categorizedTypes = useMemo(
    () => groupPreferencesByCategory(preferences),
    [preferences],
  );

  const [draft, setDraft] = useState<PreferenceMap>({});

  useEffect(() => {
    setDraft(serverPreferences);
  }, [serverPreferences]);

  const isDirty = !mapsEqual(draft, serverPreferences);

  const handleToggle = (type: string, enabled: boolean) => {
    setDraft((prev) => ({ ...prev, [type]: enabled }));
  };

  const handleSave = async () => {
    try {
      await updatePreferences.mutateAsync(draft);
      showSuccess(SUCCESS.NOTIFICATIONS.UPDATED);
    } catch (err) {
      showErrorWithApiError(err, ERROR.NOTIFICATIONS.UPDATE);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center gap-2 py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm">Loading notification preferences...</p>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyView
        icon={Bell}
        title="No notification preferences found."
        description="You can manage your notification preferences here."
        actionLabel="Retry"
        onAction={() => refetch()}
      />
    );
  }

  if (!preferences?.length) {
    return (
      <EmptyView
        icon={Bell}
        title="No notification preferences available."
        description="There are no notification types configured for your account yet."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 flex items-start gap-4 rounded-lg border border-border bg-muted">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <h2 className="text-2xl font-bold">Notifications</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Choose which workflow emails you want to receive. Notifications are opt-in so you are not
            flooded by default.
          </p>
        </div>
      </div>

      {categorizedTypes.map(({ key, title, description, items }) => (
        <Card key={key}>
          <CardHeader>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent className="divide-y">
            {items.map((item) => (
              <div
                key={item.type}
                className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="space-y-1 min-w-0 pr-4">
                  <Label htmlFor={`notification-${item.type}`} className="text-sm font-medium">
                    {item.title}
                  </Label>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                <Switch
                  id={`notification-${item.type}`}
                  checked={Boolean(draft[item.type])}
                  onCheckedChange={(checked) => handleToggle(item.type, checked)}
                  disabled={updatePreferences.isPending}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || updatePreferences.isPending}
        >
          {updatePreferences.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save preferences
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
